/** @jsx jsx */
import { jsx } from "theme-ui"
import { useState, useEffect, useRef } from "react"

import PropTypes from "prop-types"

import { TransformLinkUri } from "./link"
import Link from "./link"
import { ab } from "../theme"
import { colors } from "../theme"

const LanguageSwitcher = ({
  currentLanguage,
  allLanguage,
  currentURL,
  locationText,
  locationLink,
  alwaysDropdown = false,
}) => {
  const [languages, setLanguages] = useState(allLanguage)
  const [isOpen, setIsOpen] = useState(false)
  const listRef = useRef()
  const triggerRef = useRef()
  let locationURL

  const defaultLang = process.env.GATSBY_DEFAULT_LANG
    ? process.env.GATSBY_DEFAULT_LANG
    : "de"

  useEffect(() => {
    setLanguages(allLanguage)
  }, [allLanguage])

  // Cookie-Funktion hinzufügen
  const setLanguageCookie = (lang) => {
    const date = new Date()
    date.setTime(date.getTime() + 365 * 24 * 60 * 60 * 1000) // 1 Jahr
    document.cookie = `nf_country=${lang};expires=${date.toUTCString()};path=/;SameSite=Lax`
  }

  // Normalize empty prefix to defaultLang and deduplicate. Computed once here so
  // both the render and the dropdown-threshold logic share the same list.
  const rawLangs =
    languages !== undefined
      ? Object.values(languages).map((l) => (l === "" ? defaultLang : l))
      : []
  const langArry = rawLangs.filter(
    (lang, index) => rawLangs.indexOf(lang) === index
  )

  // Dropdown-Picker nur ab > 3 Sprachen (oder erzwungen via alwaysDropdown, z.B.
  // in der mobilen Header-Variante). Sonst bleibt die bisherige Inline-Liste.
  const asDropdown = alwaysDropdown || langArry.length > 3

  const toggleOpen = () => setIsOpen((prev) => !prev)

  // Im Dropdown-Modus schließen bei Klick außerhalb, beim Scrollen oder mit
  // Escape. Escape gibt den Fokus an den Trigger zurück, damit Tastatur-Nutzer
  // nicht am Ende des Dokuments landen.
  useEffect(() => {
    if (!asDropdown) return undefined

    const handleDocumentClick = (event) => {
      const current = listRef.current
      if (!current || current.contains(event.target)) return
      setIsOpen(false)
    }
    const handleScroll = () => setIsOpen(false)
    const handleKey = (event) => {
      if (event.key !== "Escape") return
      setIsOpen(false)
      if (triggerRef.current) triggerRef.current.focus()
    }

    document.addEventListener("click", handleDocumentClick)
    window.addEventListener("scroll", handleScroll, { passive: true })
    document.addEventListener("keydown", handleKey)

    return () => {
      document.removeEventListener("click", handleDocumentClick)
      window.removeEventListener("scroll", handleScroll)
      document.removeEventListener("keydown", handleKey)
    }
  }, [asDropdown])

  const links = () => {
    if (languages !== undefined) {
      const deLink = (l, to) => (
        <Link
          to={to}
          aria-current={currentLanguage === l ? "true" : undefined}
          onClick={() => {
            setLanguageCookie(l) // Cookie beim Klick setzen
            setIsOpen(false)
          }}
          sx={{
            color: "text",
            border: 0,
            py: 0,
            px: 1,
            textDecoration: "none",
            backgroundColor: "transparent",
          }}
        >
          <span
            sx={{
              display: "inline-block",
              fontWeight: currentLanguage === l ? "bold" : "normal",
              textTransform: "capitalize",
            }}
          >
            {l}
          </span>
        </Link>
      )

      return langArry.map((l, i) => {
        const to = TransformLinkUri(currentURL, "", l)

        return (
          <li key={i} data-cy-identifier={"language-switch-" + l}>
            {deLink(l, to)}
          </li>
        )
      })
    }
  }
  locationURL = locationLink
    ? locationLink.id
      ? TransformLinkUri(locationLink.id, "", currentLanguage)
      : locationLink.url
    : ""

  return (
    <div
      sx={{
        display: "flex",
        flexDirection: ab({ _: "row", md: "column" }),
      }}
    >
      {locationText && (
        <div
          sx={{
            order: ab({ _: "1", md: "0" }),
            ml: ab({ _: "10px", md: "0" }),
            mb: ab({ _: "0", md: "4px" }),
            textAlign: "center",
          }}
        >
          <Link
            to={locationURL}
            language={currentLanguage}
            sx={{ color: colors.text, textDecoration: "none" }}
          >
            {locationText}
          </Link>
        </div>
      )}
      {asDropdown ? (
        // Aufklappbarer Picker: echter <button> als Trigger, damit der Switcher
        // per Tastatur bedienbar und für Screenreader verständlich ist. Das Menü
        // wird absolut positioniert und wächst mit der Anzahl der Sprachen —
        // kein maxHeight-Clipping wie in der früheren Accordion-Variante.
        <div
          ref={listRef}
          className={`languageSwitcher as-dropdown${isOpen ? " active" : ""}`}
          sx={{ position: "relative" }}
        >
          <button
            ref={triggerRef}
            type="button"
            onClick={toggleOpen}
            aria-haspopup="true"
            aria-expanded={isOpen}
            aria-controls="language-switcher-menu"
            className="languageSwitcherTrigger"
          >
            <span sx={{ textTransform: "capitalize", fontWeight: "bold" }}>
              {currentLanguage}
            </span>
            <span aria-hidden="true" className="languageSwitcherCaret" />
          </button>
          {isOpen && (
            <ul id="language-switcher-menu" className="languageSwitcherList">
              {links()}
            </ul>
          )}
        </div>
      ) : (
        <ul ref={listRef} className="languageSwitcherList">
          <li
            sx={{
              textTransform: "capitalize",
              fontWeight: "bold",
            }}
          >
            {currentLanguage}
          </li>
          {links()}
        </ul>
      )}
    </div>
  )
}

LanguageSwitcher.propTypes = {
  currentURL: PropTypes.string.isRequired,
  allLanguage: PropTypes.object,
  locationLink: PropTypes.object,
  locationText: PropTypes.string,
  alwaysDropdown: PropTypes.bool,
}

LanguageSwitcher.defaultProps = {
  currentURL: "",
  currentLanguage: "de",
}

export default LanguageSwitcher
